/**
 * 회원 데이터 관리 훅
 * 회원 조회, 등록, 얼굴 삭제 등의 상태 관리를 담당
 * 서버 페이지네이션 지원
 */

import { useState, useCallback } from 'react';
import { memberRepository } from '../repositories';
import { DeletedMemberExistsError } from '../repositories/MemberRepository';
import { memberService } from '../services';
import type { Member, NewMemberData, MembershipType, MemberStatus } from '../models';

export const useMemberData = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 서버 페이지네이션 상태
  const [totalMembers, setTotalMembers] = useState<number>(0);
  const [serverPage, setServerPage] = useState<number>(1);
  const [serverLimit, setServerLimit] = useState<number>(20);

  /**
   * 회원 목록 로드 (서버 페이지네이션)
   */
  const loadMembers = useCallback(async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    membershipType?: string;
    withdrawn?: boolean;
    sort?: string;
  }): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await memberRepository.getMembers({
        page: params?.page || serverPage,
        limit: params?.limit || serverLimit,
        search: params?.search,
        status: params?.status,
        membershipType: params?.membershipType,
        withdrawn: params?.withdrawn,
        sort: params?.sort,
      });
      setMembers(response.members || []);
      setTotalMembers(response.total || 0);
      if (params?.page) setServerPage(params.page);
      if (params?.limit) setServerLimit(params.limit);
    } catch (err) {
      console.error('회원 로드 실패:', err);
      setError(err instanceof Error ? err.message : '회원 정보를 불러오는데 실패했습니다');
      setMembers([]);
      setTotalMembers(0);
    } finally {
      setLoading(false);
    }
  }, [serverPage, serverLimit]);

  /**
   * 회원 상세 정보 로드 (출입 기록 포함)
   */
  const loadMemberDetail = useCallback(
    async (memberIdentifier: string): Promise<Member | null> => {
      try {
        setLoadingDetail(true);
        const memberDetail = await memberRepository.getMemberDetail(memberIdentifier);
        setSelectedMember(memberDetail);
        return memberDetail;
      } catch (err) {
        console.error('회원 상세 정보 로드 실패:', err);
        alert('회원 상세 정보를 불러오는데 실패했습니다');
        return null;
      } finally {
        setLoadingDetail(false);
      }
    },
    []
  );

  /**
   * 새 회원 등록
   */
  const createMember = useCallback(
    async (
      memberData: NewMemberData,
      onDeletedMemberExists?: (err: DeletedMemberExistsError) => void
    ): Promise<boolean> => {
      // 유효성 검증
      const validation = memberService.validateMemberData(memberData);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      try {
        await memberRepository.createMember(memberData);
        alert('회원이 성공적으로 등록되었습니다.');
        await loadMembers();
        return true;
      } catch (err) {
        // 퇴원한 기존 회원 — 화면이 비교 창을 띄워 원장이 항목별로 고른다 (2026-10-28)
        if (err instanceof DeletedMemberExistsError && onDeletedMemberExists) {
          onDeletedMemberExists(err);
          return false;
        }
        console.error('회원 추가 실패:', err);
        alert(err instanceof Error ? err.message : '회원 추가에 실패했습니다');
        return false;
      }
    },
    [loadMembers]
  );

  /**
   * 회원 얼굴 삭제
   */
  const deleteMemberFace = useCallback(
    async (member: Member): Promise<boolean> => {
      if (
        !window.confirm(
          `${member.name}님의 등록된 얼굴을 삭제하시겠습니까?\n\n삭제 후에는 얼굴 인식으로 출입할 수 없습니다.`
        )
      ) {
        return false;
      }

      try {
        await memberRepository.deleteMemberFace(member);

        // 성공 시 회원 목록 새로고침
        await loadMembers();

        // 선택된 회원의 face_registered 상태도 업데이트
        if (selectedMember && selectedMember.id === member.id) {
          setSelectedMember({
            ...selectedMember,
            face_registered: false,
          });
        }

        alert(`${member.name}님의 얼굴이 성공적으로 삭제되었습니다.`);
        return true;
      } catch (err) {
        console.error('얼굴 삭제 실패:', err);
        alert(err instanceof Error ? err.message : '얼굴 삭제에 실패했습니다');
        return false;
      }
    },
    [loadMembers, selectedMember]
  );

  /**
   * 회원 필터링 (검색, 상태, 이용권)
   */
  const filterMembers = useCallback(
    (
      searchTerm: string,
      filterStatus: 'all' | MemberStatus,
      filterMembership: 'all' | MembershipType
    ): Member[] => {
      return memberService.filterMembers(members, searchTerm, filterStatus, filterMembership);
    },
    [members]
  );

  /**
   * 회원 통계 계산
   */
  const getMemberStats = useCallback(() => {
    return memberService.calculateMemberStats(members);
  }, [members]);

  return {
    // 상태
    members,
    selectedMember,
    loading,
    loadingDetail,
    error,
    totalMembers,
    serverPage,
    serverLimit,

    // 함수
    loadMembers,
    loadMemberDetail,
    createMember,
    deleteMemberFace,
    filterMembers,
    getMemberStats,
    setSelectedMember,
  };
};
